import { logger } from "@/utils/logger";

import { getRecentResponse } from './recentResponseUtils';
import { supabase } from '@/integrations/supabase/client';

// Define mandatory keys that must be present in a valid OpenAI response
// Only Reply_Message is truly required - all other fields are optional
const MANDATORY_KEYS = ['Reply_Message'];

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateOpenAIResponse = (response: any): ValidationResult => {
  logger.log('🔍 Validating OpenAI response structure...');
  
  // Check if response is an object
  if (!response || typeof response !== 'object') {
    logger.error('❌ Response is not a valid object:', response);
    return {
      isValid: false,
      error: 'Response is not a valid JSON object'
    };
  }
  
  // Check for mandatory keys
  const missingKeys = MANDATORY_KEYS.filter(key => !(key in response));
  
  if (missingKeys.length > 0) {
    logger.error('❌ Missing mandatory keys:', missingKeys);
    return {
      isValid: false,
      error: `Missing mandatory keys: ${missingKeys.join(', ')}`
    };
  }
  
  logger.log('✅ Response validation passed');
  return { isValid: true };
};

export const handleInvalidResponse = async (error: string, dealerId?: string): Promise<void> => {
  logger.error('🚨 Invalid OpenAI response detected:', error);
  
  // Get dealer ID from DealerManager if not provided
  if (!dealerId) {
    try {
      const { DealerManager } = await import('./dealerManager');
      const dealerInfo = DealerManager.getDealerInfo();
      dealerId = dealerInfo?.dealer_id?.toString() || 'Unknown';
      logger.log('🔍 Retrieved dealer ID from DealerManager:', dealerId);
    } catch (error) {
      logger.error('❌ Failed to get dealer ID from DealerManager:', error);
      dealerId = 'Unknown';
    }
  }
  
  // Get the received message for the error report
  const receivedMessage = await getRecentResponse();
  
  // Try to get session ID for better tracking  
  let sessionId = 'Unknown';
  try {
    const { getCurrentSession } = await import('./sessionUtils');
    const session = await getCurrentSession();
    sessionId = session?.sessionId || 'Unknown';
  } catch (error) {
    logger.warn('Could not retrieve session ID for error report:', error);
  }
  
  logger.log('📧 Preparing error email with:');
  logger.log('- Received message:', receivedMessage?.substring(0, 100) + '...');
  logger.log('- Session ID:', sessionId);
  logger.log('- Dealer ID:', dealerId || 'Unknown');
  
  // Determine subject based on error type
  let subject = `D2C Chatbot Boost error: ${dealerId || 'Unknown'}`;
  if (error.includes('connection failed') || error.includes('retries')) {
    subject += ' - Connection Failure';
  } else if (error.includes('Invalid JSON') || error.includes('plain text')) {
    subject += ' - Invalid JSON Response';
  } else {
    subject += ' - Unknown Error';
  }
  
  const emailData = {
    to: 'ddodge@carscommerce.inc',
    subject: subject,
    sentMessage: 'Not available after Cmd+Shift+S removal',
    receivedMessage: receivedMessage || 'No recent received message found',
    error: error,
    dealerId: dealerId || 'Unknown',
    sessionId: sessionId,
    timestamp: new Date().toISOString()
  };
  
  logger.log('📧 Sending error email:', emailData);
  
  try {
    // SECURITY: Use the supabase client instead of hardcoded authorization
    const { data, error } = await supabase.functions.invoke('send-error-email', {
      body: emailData
    });

    if (error) {
      logger.error('❌ Failed to send error email:', error);
    } else {
      logger.log('✅ Error email sent successfully');
    }
  } catch (emailError) {
    logger.error('❌ Error sending email:', emailError);
    logger.warn('⚠️ Email sending failed, logging to console instead:', emailData);
  }
};

export const createInvalidResponseError = (): string => {
  return 'Unexpected error - Invalid JSON object.';
};